import { useEffect, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { api, fileUrl, LANGUAGES, GENRES, TITLE_CASE_FIELDS, toTitleCase } from "../lib/api";
import { formatApiError } from "../lib/auth";
import { StatusBadge } from "../components/StatusBadge";
import { AudioPlayer } from "../components/AudioPlayer";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import { Upload, Eye, Search, Music2, Edit3, Lock, Instagram, Ban } from "lucide-react";
import { SiSpotify, SiApplemusic, SiYoutubemusic, SiYoutube, SiInstagram, SiTiktok, SiDeezer, SiSoundcloud, SiTidal, SiShazam, SiPandora, SiAudiomack, SiBandcamp, SiNapster, SiBeatport, SiIheartradio, SiMixcloud } from "react-icons/si";
import { TakedownFormDialog } from "./Takedown";

const PLATFORM_LABELS = {
  amazonMusic: "Amazon Music", amazonPrime: "Amazon Prime Music",
  anghami: "Anghami", appleMusic: "Apple Music",
  audiomack: "Audiomack", audius: "Audius", awa: "AWA", bandcamp: "Bandcamp",
  beatport: "Beatport", boomplay: "Boomplay", deezer: "Deezer", discogs: "Discogs",
  flo: "FLO", gaana: "Gaana", genius: "Genius", iHeartRadio: "iHeartRadio",
  instagram: "Instagram", jioSaavn: "JioSaavn", joox: "JOOX", kkbox: "KKBOX",
  lineMusic: "LINE Music", napster: "Napster", netease: "Netease",
  musicBrainz: "MusicBrainz", pandora: "Pandora", qobuz: "Qobuz", qqMusic: "QQ Music",
  sevenDigital: "7digital", shazam: "Shazam", soundcloud: "SoundCloud",
  spotify: "Spotify", tiktok: "TikTok", tidal: "TIDAL", trebel: "Trebel",
  yandex: "Yandex", youseeMusik: "YouSee Musik", youtube: "YouTube",
  youtubeMusic: "YouTube Music",
};

const DSP_BRAND_ICONS = {
  spotify: SiSpotify,
  appleMusic: SiApplemusic,
  youtube: SiYoutube,
  youtubeMusic: SiYoutubemusic,
  instagram: SiInstagram,
  tiktok: SiTiktok,
  deezer: SiDeezer,
  soundcloud: SiSoundcloud,
  tidal: SiTidal,
  shazam: SiShazam,
  pandora: SiPandora,
  audiomack: SiAudiomack,
  bandcamp: SiBandcamp,
  napster: SiNapster,
  beatport: SiBeatport,
  iHeartRadio: SiIheartradio,
  mixcloud: SiMixcloud,
};

const DSP_ICON_COLORS = {
  spotify: "#1DB954", appleMusic: "#FA243C", youtube: "#FF0000",
  youtubeMusic: "#FF0000", instagram: "#E4405F", tiktok: "#ffffff",
  deezer: "#A238FF", soundcloud: "#FF5500", tidal: "#ffffff",
  shazam: "#0088FF", pandora: "#00A0EE", audiomack: "#FFA200",
  bandcamp: "#629AA9", napster: "#ffffff", beatport: "#01FF95",
  iHeartRadio: "#C6002B", mixcloud: "#5000FF",
};

const DSP_COLORS = {
  spotify: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  appleMusic: "bg-rose-500/15 text-rose-400 border-rose-500/30",
  youtube: "bg-red-500/15 text-red-400 border-red-500/30",
  youtubeMusic: "bg-red-500/15 text-red-400 border-red-500/30",
  instagram: "bg-fuchsia-500/15 text-fuchsia-400 border-fuchsia-500/30",
  jioSaavn: "bg-green-500/15 text-green-400 border-green-500/30",
  amazonMusic: "bg-sky-500/15 text-sky-400 border-sky-500/30",
  gaana: "bg-red-400/15 text-red-300 border-red-400/30",
  tiktok: "bg-zinc-500/15 text-zinc-200 border-zinc-500/30",
};

function DspCell({ links, songId }) {
  const entries = Object.entries(links || {}).filter(([, url]) => url);
  if (!entries.length) {
    return (
      <span className="text-[11px] text-zinc-500 whitespace-nowrap" data-testid={`dsp-empty-${songId}`}>
        Not Available Yet
      </span>
    );
  }
  return (
    <div className="flex flex-row flex-nowrap items-center gap-1.5" data-testid={`dsp-cell-${songId}`}>
      {entries.map(([k, url]) => {
        const Icon = DSP_BRAND_ICONS[k];
        const label = PLATFORM_LABELS[k] || k;
        const color = DSP_COLORS[k] || "bg-zinc-800/60 text-zinc-300 border-zinc-700";
        return (
          <a key={k} href={url} target="_blank" rel="noreferrer" title={label}
            data-testid={`dsp-icon-${songId}-${k}`}
            style={Icon ? { color: DSP_ICON_COLORS[k] || "#e4e4e7" } : undefined}
            className={`shrink-0 w-6 h-6 rounded-full border inline-flex items-center justify-center transition-transform hover:scale-110 hover:brightness-125 ${Icon ? "bg-zinc-900/80 border-zinc-700 hover:border-zinc-500" : color}`}>
            {Icon
              ? <Icon className="w-3.5 h-3.5" />
              : <span className="text-[9px] font-bold uppercase leading-none">{label.slice(0, 2)}</span>}
          </a>
        );
      })}
    </div>
  );
}

export default function MySongs() {
  const location = useLocation();
  const [subs, setSubs] = useState([]);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [editing, setEditing] = useState(false);
  const [tdOpen, setTdOpen] = useState(false);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 20;

  const load = () => api.get("/submissions", { params: { q } }).then((r) => setSubs(r.data));
  useEffect(() => { setPage(1); const t = setTimeout(load, 200); return () => clearTimeout(t); }, [q]);

  useEffect(() => {
    if (location.state?.open) {
      api.get(`/submissions/${location.state.open}`).then((r) => setSelected(r.data)).catch(() => {});
    }
  }, [location.state]);

  const tcBlur = (k) => () => setSelected((s) => (s && TITLE_CASE_FIELDS.includes(k) && s[k] ? { ...s, [k]: toTitleCase(s[k]) } : s));

  const save = async () => {
    const { id, isrc, upc, status, user_id, artwork_url, audio_url, audio_format, created_at, last_edited_at, pending_review, change_history, artwork_drive_url, audio_drive_url, ...allowed } = selected;
    TITLE_CASE_FIELDS.forEach((k) => { if (allowed[k]) allowed[k] = toTitleCase(allowed[k]); });
    try {
      await api.patch(`/submissions/${id}`, allowed);
      toast.success("Changes saved. Admin will review.");
      setEditing(false);
      const fresh = await api.get(`/submissions/${id}`).then(r => r.data);
      setSelected(fresh);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <div className="text-xs font-semibold tracking-[0.2em] uppercase text-cyan-400">Your Catalog</div>
          <h1 className="mt-2 text-4xl font-bold tracking-tight">My Songs</h1>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
            <Input placeholder="Search by Song, Singer, ISRC or UPC" value={q} onChange={(e) => setQ(e.target.value)}
              data-testid="mysongs-search"
              className="pl-9 bg-zinc-900 border-zinc-800 w-80" />
          </div>
          <Link to="/upload">
            <Button className="rounded-full bg-gradient-to-r from-cyan-400 to-violet-500 text-zinc-950 font-semibold hover:brightness-110" data-testid="mysongs-upload-btn">
              <Upload className="w-4 h-4 mr-2" /> Upload
            </Button>
          </Link>
        </div>
      </div>

      {subs.length === 0 ? (
        <div className="p-16 rounded-2xl border border-zinc-800 bg-zinc-950/60 text-center">
          <Music2 className="w-10 h-10 text-zinc-600 mx-auto mb-4" />
          <p className="text-zinc-400">{q ? "No matches found" : "No submissions yet."}</p>
        </div>
      ) : (
        (() => {
          const totalPages = Math.max(1, Math.ceil(subs.length / PAGE_SIZE));
          const cur = Math.min(page, totalPages);
          const pageRows = subs.slice((cur - 1) * PAGE_SIZE, cur * PAGE_SIZE);
          const from = (cur - 1) * PAGE_SIZE + 1;
          const to = Math.min(cur * PAGE_SIZE, subs.length);
          return (
            <div className="rounded-2xl border border-zinc-800 bg-zinc-950/60 overflow-hidden" data-testid="mysongs-table-wrap">
              <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[1150px]">
                  <thead>
                    <tr className="border-b border-zinc-800 text-left text-[11px] uppercase tracking-wider text-zinc-500">
                      <th className="px-4 py-3 font-medium w-14">Artwork</th>
                      <th className="px-4 py-3 font-medium">Song</th>
                      <th className="px-4 py-3 font-medium">Singer</th>
                      <th className="px-4 py-3 font-medium">ISRC</th>
                      <th className="px-4 py-3 font-medium">UPC</th>
                      <th className="px-4 py-3 font-medium">Release Date</th>
                      <th className="px-4 py-3 font-medium">Status</th>
                      <th className="px-4 py-3 font-medium">DSP Links</th>
                      <th className="px-4 py-3 font-medium text-right">Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pageRows.map((s) => (
                      <tr key={s.id} data-testid={`song-row-${s.id}`}
                        className="border-b border-zinc-800/60 hover:bg-zinc-900/40 transition-colors">
                        <td className="px-4 py-2.5">
                          <img src={fileUrl(s.artwork_url)} alt={s.song_title}
                            className="w-11 h-11 rounded-md object-cover border border-zinc-800 bg-zinc-900" />
                        </td>
                        <td className="px-4 py-2.5 min-w-[180px]">
                          <div className="font-semibold text-zinc-100 truncate max-w-[260px]">{s.song_title}</div>
                          <div className="text-xs text-zinc-500 truncate max-w-[260px]">{s.primary_artist_name || s.artist_name || "—"}</div>
                          {s.pending_review && (
                            <span className="mt-1 inline-block px-1.5 py-0.5 rounded bg-amber-500/15 border border-amber-500/40 text-amber-300 text-[9px] uppercase tracking-wider font-semibold">
                              Recently Edited
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-2.5 text-xs text-zinc-300 whitespace-nowrap max-w-[140px] truncate" data-testid={`singer-${s.id}`}>{s.singer_name || "—"}</td>
                        <td className="px-4 py-2.5 font-mono text-xs text-zinc-400 whitespace-nowrap">{s.isrc || "—"}</td>
                        <td className="px-4 py-2.5 font-mono text-xs text-zinc-400 whitespace-nowrap">{s.upc || "—"}</td>
                        <td className="px-4 py-2.5 text-xs text-zinc-400 whitespace-nowrap">{s.release_date || new Date(s.created_at).toLocaleDateString()}</td>
                        <td className="px-4 py-2.5"><StatusBadge status={s.status} /></td>
                        <td className="px-4 py-2.5"><DspCell links={s.dsp_links} songId={s.id} /></td>
                        <td className="px-4 py-2.5 text-right">
                          <Button size="sm" variant="ghost" className="text-cyan-400 hover:text-cyan-300 h-7"
                            onClick={() => { setSelected(s); setEditing(false); }} data-testid={`view-details-${s.id}`}>
                            <Eye className="w-3.5 h-3.5 mr-1" /> View Details
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-t border-zinc-800"
                data-testid="mysongs-pagination">
                <span className="text-xs text-zinc-500">
                  Showing {from}–{to} of {subs.length} songs
                </span>
                <div className="flex items-center gap-1">
                  <Button size="sm" variant="outline" disabled={cur <= 1} onClick={() => setPage(cur - 1)}
                    className="h-7 border-zinc-700 text-xs" data-testid="page-prev">
                    Previous
                  </Button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1)
                    .filter((p) => p === 1 || p === totalPages || Math.abs(p - cur) <= 1)
                    .reduce((acc, p, idx, arr) => {
                      if (idx > 0 && p - arr[idx - 1] > 1) acc.push("…");
                      acc.push(p);
                      return acc;
                    }, [])
                    .map((p, idx) =>
                      p === "…" ? (
                        <span key={`ellipsis-${idx}`} className="px-1 text-zinc-600 text-xs">…</span>
                      ) : (
                        <Button key={p} size="sm" variant={p === cur ? "default" : "outline"}
                          onClick={() => setPage(p)}
                          className={`h-7 w-7 p-0 text-xs ${p === cur ? "bg-cyan-500 text-zinc-950 hover:bg-cyan-400" : "border-zinc-700"}`}
                          data-testid={`page-${p}`}>
                          {p}
                        </Button>
                      )
                    )}
                  <Button size="sm" variant="outline" disabled={cur >= totalPages} onClick={() => setPage(cur + 1)}
                    className="h-7 border-zinc-700 text-xs" data-testid="page-next">
                    Next
                  </Button>
                </div>
              </div>
            </div>
          );
        })()
      )}

      <Dialog open={!!selected} onOpenChange={(o) => { if (!o) { setSelected(null); setEditing(false); }}}>
        <DialogContent className="max-w-3xl bg-zinc-950 border-zinc-800 max-h-[90vh] overflow-y-auto" data-testid="song-details-dialog">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="text-2xl flex items-center gap-3">
                  {selected.song_title}
                  <StatusBadge status={selected.status} />
                </DialogTitle>
              </DialogHeader>
              <div className="grid md:grid-cols-2 gap-6 mt-4">
                <div>
                  <img src={fileUrl(selected.artwork_url)} className="w-full aspect-square rounded-lg object-cover border border-zinc-800" alt="art" />
                  <div className="mt-4">
                    <AudioPlayer src={selected.audio_url} testId="details-player" />
                  </div>
                </div>
                <div className="space-y-2 text-sm">
                  {editing ? (
                    <>
                      <Field label="Song Title"><Input data-testid="edit-song-title" value={selected.song_title||""} onChange={(e)=>setSelected({...selected, song_title:e.target.value})} onBlur={tcBlur("song_title")} className="bg-zinc-900 border-zinc-800" /></Field>
                      <Field label="Primary Artist"><Input data-testid="edit-primary-artist" value={selected.primary_artist_name||""} onChange={(e)=>setSelected({...selected, primary_artist_name:e.target.value})} onBlur={tcBlur("primary_artist_name")} className="bg-zinc-900 border-zinc-800" /></Field>
                      <Field label="Instagram"><Input data-testid="edit-instagram" value={selected.primary_artist_instagram||""} onChange={(e)=>setSelected({...selected, primary_artist_instagram:e.target.value})} className="bg-zinc-900 border-zinc-800" /></Field>
                      <Field label="Spotify"><Input data-testid="edit-spotify" value={selected.primary_artist_spotify||""} onChange={(e)=>setSelected({...selected, primary_artist_spotify:e.target.value})} className="bg-zinc-900 border-zinc-800" /></Field>
                      <Field label="Apple Music"><Input data-testid="edit-apple-music" value={selected.primary_artist_apple_music||""} onChange={(e)=>setSelected({...selected, primary_artist_apple_music:e.target.value})} placeholder="Artist URL or ID" className="bg-zinc-900 border-zinc-800" /></Field>
                      <Field label="Featured Artist"><Input value={selected.artist_name||""} onChange={(e)=>setSelected({...selected, artist_name:e.target.value})} onBlur={tcBlur("artist_name")} className="bg-zinc-900 border-zinc-800" /></Field>
                      <Field label="Singer"><Input value={selected.singer_name||""} onChange={(e)=>setSelected({...selected, singer_name:e.target.value})} onBlur={tcBlur("singer_name")} className="bg-zinc-900 border-zinc-800" /></Field>
                      <Field label="Composer"><Input value={selected.composer_name||""} onChange={(e)=>setSelected({...selected, composer_name:e.target.value})} onBlur={tcBlur("composer_name")} className="bg-zinc-900 border-zinc-800" /></Field>
                      <Field label="Lyricist"><Input value={selected.lyricist_name||""} onChange={(e)=>setSelected({...selected, lyricist_name:e.target.value})} onBlur={tcBlur("lyricist_name")} className="bg-zinc-900 border-zinc-800" /></Field>
                      <Field label="Language">
                        <Select value={selected.language||""} onValueChange={(v)=>setSelected({...selected, language:v})}>
                          <SelectTrigger className="bg-zinc-900 border-zinc-800"><SelectValue placeholder="—" /></SelectTrigger>
                          <SelectContent>{LANGUAGES.map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
                        </Select>
                      </Field>
                      <Field label="Genre">
                        <Select value={selected.genre||""} onValueChange={(v)=>setSelected({...selected, genre:v})}>
                          <SelectTrigger className="bg-zinc-900 border-zinc-800"><SelectValue placeholder="—" /></SelectTrigger>
                          <SelectContent>{GENRES.map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
                        </Select>
                      </Field>
                      <Field label="Release Date"><Input type="date" value={selected.release_date||""} onChange={(e)=>setSelected({...selected, release_date:e.target.value})} className="bg-zinc-900 border-zinc-800" /></Field>
                      <Field label="Notes"><Textarea rows={2} value={selected.notes||""} onChange={(e)=>setSelected({...selected, notes:e.target.value})} className="bg-zinc-900 border-zinc-800" /></Field>
                      <div className="mt-2 p-3 rounded-md bg-zinc-900 border border-zinc-800 text-xs text-zinc-500 flex items-center gap-2">
                        <Lock className="w-3.5 h-3.5" /> ISRC, UPC, Status, and Revenue are managed by admin.
                      </div>
                    </>
                  ) : (
                    <>
                      <Row k="Submission ID" v={<code className="text-cyan-400 text-xs">{selected.id}</code>} />
                      <Row k="Primary Artist" v={selected.primary_artist_name} />
                      <Row k="Instagram" v={selected.primary_artist_instagram && <a href={selected.primary_artist_instagram.startsWith('http') ? selected.primary_artist_instagram : `https://instagram.com/${selected.primary_artist_instagram.replace('@','')}`} target="_blank" rel="noreferrer" className="text-cyan-400 hover:underline inline-flex items-center gap-1"><Instagram className="w-3 h-3" />{selected.primary_artist_instagram}</a>} />
                      <Row k="Spotify" v={selected.primary_artist_spotify} />
                      <Row k="Apple Music" v={selected.primary_artist_apple_music} />
                      <Row k="Featured Artist" v={selected.artist_name} />
                      <Row k="Singer" v={selected.singer_name} />
                      <Row k="Composer" v={selected.composer_name} />
                      <Row k="Lyricist" v={selected.lyricist_name} />
                      <Row k="Music Director" v={selected.music_director} />
                      <Row k="Label" v={selected.label_name} />
                      <Row k="Language" v={selected.language} />
                      <Row k="Genre" v={selected.genre} />
                      <Row k="Release Date" v={selected.release_date} />
                      <Row k="ISRC" v={<code className="text-emerald-400">{selected.isrc || "—"}</code>} />
                      <Row k="UPC" v={<code className="text-emerald-400">{selected.upc || "—"}</code>} />
                      <Row k="Format" v={selected.audio_format} />
                      {selected.notes && <div className="pt-3 border-t border-zinc-800 text-zinc-400">{selected.notes}</div>}
                      {selected.dsp_links && Object.keys(selected.dsp_links).length > 0 && (
                        <div className="pt-3 border-t border-zinc-800" data-testid="available-on">
                          <div className="text-xs uppercase tracking-wider text-zinc-500 mb-2">Available On / Listen On</div>
                          <div className="flex flex-wrap gap-1.5">
                            {Object.entries(selected.dsp_links).map(([k, url]) => (
                              <a key={k} href={url} target="_blank" rel="noreferrer"
                                data-testid={`dsp-link-${k}`}
                                className="px-2.5 py-1 rounded-full bg-zinc-900 border border-zinc-800 hover:border-cyan-500/50 hover:text-cyan-400 text-xs text-zinc-300 transition-colors">
                                {PLATFORM_LABELS[k] || k}
                              </a>
                            ))}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
              <DialogFooter className="mt-6">
                {editing ? (
                  <>
                    <Button variant="outline" onClick={() => setEditing(false)} className="border-zinc-700 hover:bg-zinc-900">Cancel</Button>
                    <Button onClick={save} data-testid="edit-save-btn" className="bg-gradient-to-r from-cyan-400 to-violet-500 text-zinc-950 font-semibold">Save Changes</Button>
                  </>
                ) : (
                  <div className="flex flex-wrap gap-2 justify-end w-full">
                    {selected.takedown_status && ["Pending", "In Review", "In Process"].includes(selected.takedown_status) ? (
                      <div className="px-3 py-2 rounded-lg bg-rose-500/10 border border-rose-500/30 text-rose-300 text-xs inline-flex items-center gap-2" data-testid="takedown-active-banner">
                        <Ban className="w-3.5 h-3.5" /> Takedown Requested – {selected.takedown_status}
                      </div>
                    ) : (
                      <Button onClick={() => setTdOpen(true)} data-testid="request-takedown-btn"
                        variant="outline" className="border-rose-500/40 text-rose-400 hover:bg-rose-500/10 rounded-full">
                        <Ban className="w-4 h-4 mr-2" /> Request Takedown
                      </Button>
                    )}
                    <Button onClick={() => setEditing(true)} data-testid="edit-song-btn"
                      className="bg-gradient-to-r from-cyan-400 to-violet-500 text-zinc-950 font-semibold rounded-full">
                      <Edit3 className="w-4 h-4 mr-2" /> Edit Song
                    </Button>
                  </div>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      <TakedownFormDialog open={tdOpen} onOpenChange={setTdOpen} song={selected}
        onDone={async () => {
          if (selected) {
            const fresh = await api.get(`/submissions/${selected.id}`).then(r => r.data);
            setSelected(fresh);
          }
          load();
        }} />
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex justify-between gap-4">
      <span className="text-zinc-500 uppercase tracking-wider text-xs">{k}</span>
      <span className="text-zinc-200 text-right">{v || "—"}</span>
    </div>
  );
}
function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-wider text-zinc-400">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
